import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useBarcodeScanner } from '../../hooks/useBarcodeScanner';
import { useScanSound } from '../../hooks/useScanSound';
import { usePOSCart } from '../../hooks/usePOSCart';
import apiService from '../../services/api';
import POSProductGrid from '../../components/pos/POSProductGrid';
import POSCartPanel from '../../components/pos/POSCartPanel';
import POSPaymentModal from '../../components/pos/POSPaymentModal';
import POSVariantModal from '../../components/pos/POSVariantModal';
import POSReceiptModal from '../../components/pos/POSReceiptModal';

export interface POSProduct {
  id: number;
  name: string;
  product_code: string;
  barcode: string | null;
  price: number;
  selling_price: number;
  promotional_price: number | null;
  has_promotion: boolean;
  cost_price: number;
  image: string | null;
  category_id: number | null;
  category_name: string | null;
  brand_name: string | null;
  total_stock: number;
  variants: POSVariant[];
  has_variants: boolean;
}

export interface POSVariant {
  id: number;
  sku: string;
  color: string;
  color_code: string | null;
  price: number | null;
  is_active: boolean;
  sizes: { name: string; stock_quantity: number; price?: number | null }[];
}

const POSTerminal: React.FC = () => {
  const [products, setProducts] = useState<POSProduct[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<number | null>(null);
  const [categories, setCategories] = useState<any[]>([]);
  const [showPayment, setShowPayment] = useState(false);
  const [showVariantModal, setShowVariantModal] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<POSProduct | null>(null);
  const [completedSale, setCompletedSale] = useState<any>(null);

  const cart = usePOSCart();
  const { playSuccess, playError, playAddItem } = useScanSound();
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Load products
  const fetchProducts = useCallback(async (search?: string, categoryId?: number | null) => {
    try {
      setLoading(true);
      const params: any = { per_page: 100 };
      if (search) params.search = search;
      if (categoryId) params.category_id = categoryId;

      const response = await apiService.get('/pos/products', { params });
      setProducts(response.data || []);
    } catch (error) {
      console.error('Failed to load POS products:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  // Load categories + initial products
  useEffect(() => {
    const loadCategories = async () => {
      try {
        const response = await apiService.get('/categories/public');
        setCategories(Array.isArray(response) ? response : response.data || []);
      } catch {}
    };
    loadCategories();
    // NOTE: do NOT call fetchProducts() here. The debounced search/category
    // effect below handles the initial load (with searchQuery='' and
    // selectedCategory=null). Calling it here as well causes a double fetch
    // and a visible double loading-flash on the POS grid.

    // Check for resumed sale from held sales page
    const resumedData = sessionStorage.getItem('pos_resumed_sale');
    if (resumedData) {
      sessionStorage.removeItem('pos_resumed_sale');
      try {
        const parsed = JSON.parse(resumedData);
        const items = parsed.items || parsed;
        if (Array.isArray(items) && items.length > 0) {
          const cartItems = items.map((item: any) => ({
            key: `${item.product_id}:${item.variant_id || ''}:${item.size_name || ''}`,
            productId: item.product_id,
            variantId: item.variant_id || undefined,
            sizeName: item.size_name || undefined,
            productName: item.product_name,
            productImage: item.product_image || undefined,
            sku: item.sku || '',
            unitPrice: Number(item.unit_price),
            costPrice: Number(item.cost_price || 0),
            quantity: item.quantity,
            discount: Number(item.discount || 0),
            maxStock: 999,
            color: undefined,
          }));
          cart.loadItems(cartItems, {
            customerName: parsed.customer_name || '',
            customerPhone: parsed.customer_phone || '',
            notes: parsed.notes || '',
            orderDiscount: Number(parsed.order_discount || 0),
          });
        }
      } catch (e) {
        console.error('Failed to load resumed sale:', e);
      }
    }
  }, [fetchProducts]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch products: immediate on mount, debounced when search/category changes
  const isFirstLoad = useRef(true);
  useEffect(() => {
    const delay = isFirstLoad.current ? 0 : 300;
    isFirstLoad.current = false;
    const timer = setTimeout(() => {
      fetchProducts(searchQuery, selectedCategory);
    }, delay);
    return () => clearTimeout(timer);
  }, [searchQuery, selectedCategory, fetchProducts]);

  // Handle barcode scan
  const handleBarcodeScan = useCallback(async (barcode: string) => {
    try {
      const response = await apiService.post('/pos/products/barcode', { barcode });
      if (response.data?.found && response.data?.product) {
        const product = response.data.product as POSProduct;
        handleAddProduct(product);
        playSuccess();
      } else {
        playError();
      }
    } catch {
      playError();
    }
  }, [playSuccess, playError]); // eslint-disable-line react-hooks/exhaustive-deps

  useBarcodeScanner({ onScan: handleBarcodeScan, enabled: !showPayment && !showVariantModal });

  // Add product to cart
  const handleAddProduct = useCallback((product: POSProduct) => {
    if (product.has_variants && product.variants.length > 0) {
      // Show variant selector
      setSelectedProduct(product);
      setShowVariantModal(true);
    } else {
      // Simple product, add directly with promotional price if available
      cart.addItem({
        productId: product.id,
        productName: product.name,
        productImage: product.image || undefined,
        sku: product.product_code,
        unitPrice: product.selling_price,
        costPrice: product.cost_price,
        maxStock: product.total_stock,
      });
      playAddItem();
    }
  }, [cart, playAddItem]);

  // Handle variant selection
  const handleVariantSelect = useCallback((product: POSProduct, variant: POSVariant, sizeName: string, stock: number) => {
    const price = variant.price || product.selling_price;
    cart.addItem({
      productId: product.id,
      variantId: variant.id,
      sizeName,
      productName: `${product.name}`,
      productImage: product.image || undefined,
      sku: variant.sku,
      unitPrice: price,
      costPrice: product.cost_price,
      maxStock: stock,
      color: variant.color,
    });
    playAddItem();
    setShowVariantModal(false);
    setSelectedProduct(null);
  }, [cart, playAddItem]);

  // Complete sale
  const handleCompleteSale = useCallback(async (paymentMethod: string, amountReceived: number) => {
    try {
      const payload = {
        items: cart.items.map(item => ({
          product_id: item.productId,
          variant_id: item.variantId || null,
          size_name: item.sizeName || null,
          quantity: item.quantity,
          unit_price: item.unitPrice,
          discount: item.discount,
        })),
        payment_method: paymentMethod,
        amount_received: amountReceived,
        order_discount: cart.orderDiscount,
        customer_name: cart.customerName || null,
        customer_phone: cart.customerPhone || null,
        notes: cart.notes || null,
      };

      const response = await apiService.post('/pos/sales', payload);
      setCompletedSale(response.data?.sale);
      setShowPayment(false);
      cart.clearCart();
      playSuccess();

      // Refresh products to update stock
      fetchProducts(searchQuery, selectedCategory);
    } catch (error: any) {
      playError();
      alert(error.response?.data?.message || 'Failed to complete sale');
    }
  }, [cart, fetchProducts, searchQuery, selectedCategory, playSuccess, playError]);

  // Hold sale
  const handleHoldSale = useCallback(async () => {
    if (cart.items.length === 0) return;

    try {
      const payload = {
        items: cart.items.map(item => ({
          product_id: item.productId,
          variant_id: item.variantId || null,
          size_name: item.sizeName || null,
          quantity: item.quantity,
          unit_price: item.unitPrice,
          discount: item.discount,
        })),
        order_discount: cart.orderDiscount,
        customer_name: cart.customerName || null,
        customer_phone: cart.customerPhone || null,
        notes: cart.notes || null,
      };

      await apiService.post('/pos/sales/hold', payload);
      cart.clearCart();
      playSuccess();
    } catch (error: any) {
      playError();
      alert(error.response?.data?.message || 'Failed to hold sale');
    }
  }, [cart, playSuccess, playError]);

  return (
    <div className="flex h-full">
      {/* Left: Product Grid */}
      <div className="flex-1 flex flex-col overflow-hidden border-r border-gray-700">
        {/* Search & Filters */}
        <div className="p-3 bg-gray-800 border-b border-gray-700 flex gap-2">
          <input
            ref={searchInputRef}
            type="text"
            placeholder="Search products, SKU, or scan barcode..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            data-barcode-target="true"
            className="flex-1 px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
          />
          <select
            value={selectedCategory || ''}
            onChange={(e) => setSelectedCategory(e.target.value ? Number(e.target.value) : null)}
            className="px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">All Categories</option>
            {categories.map((cat: any) => (
              <option key={cat.id} value={cat.id}>{cat.name}</option>
            ))}
          </select>
        </div>

        {/* Products */}
        <POSProductGrid
          products={products}
          loading={loading}
          onAddProduct={handleAddProduct}
        />
      </div>

      {/* Right: Cart */}
      <div className="w-[400px] flex flex-col bg-gray-800 shrink-0">
        <POSCartPanel
          cart={cart}
          onCheckout={() => setShowPayment(true)}
          onHold={handleHoldSale}
        />
      </div>

      {/* Payment Modal */}
      {showPayment && (
        <POSPaymentModal
          totalAmount={cart.totalAmount}
          onComplete={handleCompleteSale}
          onClose={() => setShowPayment(false)}
        />
      )}

      {/* Variant Selection Modal */}
      {showVariantModal && selectedProduct && (
        <POSVariantModal
          product={selectedProduct}
          onSelect={handleVariantSelect}
          onClose={() => { setShowVariantModal(false); setSelectedProduct(null); }}
        />
      )}

      {/* Receipt Modal */}
      {completedSale && (
        <POSReceiptModal
          sale={completedSale}
          onClose={() => setCompletedSale(null)}
        />
      )}
    </div>
  );
};

export default POSTerminal;
